/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import BaseService from './BaseService';
import {getValueFromPath, updateValueInPath} from '@exc/url';
import {history} from '@exc/router';
import metrics from '@adobe/exc-app/metrics';
import unifiedShellSession from '../UnifiedShellSessionService';

const compare = (a, b) => a.name.localeCompare(b.name);

export default class SubOrgService extends BaseService {
  isSubOrg = true;

  constructor(solutionConfig, callback) {
    super(solutionConfig, callback, 'so', 'shellSubOrgs');
    this.metrics = metrics.create('exc.core.SubOrgService');
  }

  async fetch(state) {
    await super.fetch(state);
    if (!Object.keys(state.orgToSubOrgsMap).length) {
      await this.getOrgsToSubOrgsMap();
    }

    const {selectedImsOrg, loginProductContext} = state;
    const {subOrgs, lastSelectedSubOrg} = await unifiedShellSession.get(this.storageKey, selectedImsOrg) || {};
    let selectedSubOrg;

    if (subOrgs) {
      let pathSubOrg = getValueFromPath(history.location.pathname, this.urlKey);
      pathSubOrg = pathSubOrg && subOrgs.find(so => so.orgIdName === pathSubOrg);

      if (pathSubOrg) {
        selectedSubOrg = pathSubOrg;
      } else {
        // On login, we want to select the subOrg that was returned by the SSO handler.
        selectedSubOrg = loginProductContext && subOrgs.find(so => so.id === loginProductContext);
        // Otherwise, select the last selected sub org from local storage, the default sub org, or the first in the list.
        selectedSubOrg = selectedSubOrg || lastSelectedSubOrg && subOrgs.find(so => so.name === lastSelectedSubOrg);
        selectedSubOrg = selectedSubOrg || subOrgs.find(so => so.preferred) || subOrgs.sort(compare)[0];
      }
    }

    this.store(selectedSubOrg, selectedImsOrg);
    this.set(selectedSubOrg);
  }

  async getOrgsToSubOrgsMap() {
    let localSession = await unifiedShellSession.get();
    const productContext = this.profile.projectedProductContext;
    let orgToSubOrgsMap = {};

    productContext.forEach(({prodCtx}) => {
      const {ident, owningEntity} = prodCtx;
      const {id, name, preferred, serviceCode} = this.contextConfig;

      // If the PC Entry is a subOrg, store in unifiedShellSession and form orgToSubOrgs map
      if (prodCtx[id] && prodCtx.serviceCode === (serviceCode || this.serviceCode)) {
        const formattedSubOrg = {
          id: ident,
          name: prodCtx[name],
          orgIdName: prodCtx[id],
          owningEntity,
          preferred: prodCtx[preferred]
        };

        if (localSession[this.storageKey] && localSession[this.storageKey][owningEntity]) {
          // Add to storage entry unless it already exists
          localSession[this.storageKey][owningEntity].subOrgs.find(so => so.id === ident) ||
          localSession[this.storageKey][owningEntity].subOrgs.push(formattedSubOrg);
          // Form orgs to subOrgs map
          if (!orgToSubOrgsMap[owningEntity]) {
            orgToSubOrgsMap[owningEntity] = [formattedSubOrg];
          } else {
            orgToSubOrgsMap[owningEntity].find(so => so.id === ident) ||
            orgToSubOrgsMap[owningEntity].push(formattedSubOrg);
          }
        } else {
          localSession[this.storageKey] = {
            ...localSession[this.storageKey],
            [owningEntity]: {
              subOrgs: [formattedSubOrg]
            }
          };
          orgToSubOrgsMap[owningEntity] = [formattedSubOrg];
        }
      }
    });

    // Alphabetically sort
    for (const org in orgToSubOrgsMap) {
      orgToSubOrgsMap[org].sort(compare);
    }

    this.updateState({orgToSubOrgsMap});
    unifiedShellSession.set(localSession);
  }

  set(selectedSubOrg) {
    this.updateState({selectedSubOrg});
    const {orgIdName} = selectedSubOrg || {};
    if (orgIdName !== getValueFromPath(history.location.pathname, this.urlKey)) {
      updateValueInPath(this.urlKey, orgIdName, this.hasTenantInUrl);
    }
  }

  store(selectedSubOrg, selectedImsOrg) {
    let activeProductContext = {};
    if (selectedSubOrg) {
      super.store({lastSelectedSubOrg: selectedSubOrg.name}, selectedImsOrg);
      activeProductContext = {
        [this.serviceCode]: {
          ident: selectedSubOrg.id,
          serviceCode: this.serviceCode
        }
      };
    }
    this.updateState({activeProductContext});
  }

  change(selectedImsOrg, selectedSubOrg) {
    this.set(selectedSubOrg);
    this.store(selectedSubOrg, selectedImsOrg);
  }

  onHistoryChange(location, state) {
    const {selectedSubOrg, orgToSubOrgsMap, selectedImsOrg} = state;
    const {orgIdName} = selectedSubOrg || {};
    let pathSubOrg = getValueFromPath(location.pathname, this.urlKey);

    // If the suborg is manually changed in the URL, set the new suborg.
    if (pathSubOrg && orgIdName && pathSubOrg !== orgIdName) {
      let updatedSubOrg = (orgToSubOrgsMap[selectedImsOrg] || []).find(s => s.orgIdName === pathSubOrg);
      updatedSubOrg && this.change(selectedImsOrg, updatedSubOrg);
    }

    if (orgIdName && !getValueFromPath(location.pathname, this.urlKey)) {
      updateValueInPath(this.urlKey, orgIdName, this.hasTenantInUrl);
    }
  }
}
